import { useState, useRef, useEffect, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Undo2, Search, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { fetchSidData } from '../../utils/sidBlob';
import { fetchVolumeData } from '../../utils/volumeBlob';
import { evaluateFilterExpression, parseCrossColumnReference, evaluateCrossColumn, type FilterCell, type AtomEvaluator } from '../../utils/columnFilter';
import { FilterPresets } from '../ui/filter-presets';
import { cleanFilters, settingsKey, type FilterSettings } from '../../utils/filterPresets';

// ─── Data shape ─────────────────────────────────────────────────────────────

export interface SidRowData {
  symbol: string;
  price: number;
  volume: number;
  value: number;
  percent1d: number;
  price_color: number;
  vol_color: number;
  trend: string;
  momentum: number;
  insider?: number | undefined;
  months: Record<string, number | null>; // YYYYMM → jumlah pemegang (null = "-")
}

const convertBackendToFrontend = (
  rows: Record<string, string | number>[],
  months: string[],
): SidRowData[] => {
  const uniqueItems = new Map<string, Record<string, string | number>>();
  for (const item of rows) {
    const symbol = String(item['Ticker'] || item['Stock'] || '');
    if (symbol && !uniqueItems.has(symbol)) {
      uniqueItems.set(symbol, item);
    }
  }

  return Array.from(uniqueItems.values()).map(item => {
    const symbol = String(item['Ticker'] || item['Stock'] || '');

    const monthValues: Record<string, number | null> = {};
    for (const m of months) {
      const v = item[m];
      monthValues[m] = typeof v === 'number' && isFinite(v) ? v : null;
    }

    let trend = 'SW';
    if (item['trend'] !== undefined) {
      const t = Number(item['trend']);
      if (t === 2) trend = 'Up Trend';
      else if (t === 0) trend = 'Down Trend';
    }

    return {
      symbol,
      price: Number(item['price']) || 0,
      volume: Number(item['volume']) || 0,
      value: Number(item['value']) || 0,
      percent1d: Number(item['price_chg']) || 0,
      price_color: Number(item['price_color']) || 0,
      vol_color: Number(item['vol_color']) || 0,
      trend,
      momentum: item['momentum'] !== undefined ? Number(item['momentum']) : 1,
      insider: item['insider'] !== undefined ? Number(item['insider']) : undefined,
      months: monthValues,
    };
  });
};

// ─── Formatting helpers ─────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FIXED_COLS_WIDTH = 598;
const MONTH_COL_WIDTH = 74;
const MONTH_REF_RE = /^(?:\d{6}|m\d{1,2})$/i;

const monthLabel = (ym: string): string => {
  if (ym.length !== 6) return ym;
  const mi = parseInt(ym.slice(4, 6), 10) - 1;
  return `${MONTH_NAMES[mi] ?? ''} ${ym.slice(2, 4)}`;
};

const monthAlias = (months: string[], idx: number): string => `M${months.length - 1 - idx}`;

const resolveMonthRef = (ref: string, months: string[]): string | null => {
  if (/^\d{6}$/.test(ref)) return ref;
  const k = parseInt(ref.slice(1), 10);
  if (isNaN(k) || k < 0 || k >= months.length) return null;
  return months[months.length - 1 - k] ?? null;
};

// ─── Presets (saved filter settings) ────────────────────────────────────────
// Month columns are stored relative to the newest one (M0 = newest, as the header tooltip names them),
// so a preset saved months ago lands on the same position after a new month arrives.

const REL_MONTH_RE = /^M\d{1,2}$/;
const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100, 200, 500];
const COLUMN_LABELS: Record<string, string> = {
  symbol: 'Ticker', trend: 'Trend', momentum: 'Momentum', insider: 'Insider', vs: 'VSpike',
  percent1d: '%', price: 'Price', volume: 'Volume', value: 'Value',
};

type SidSort = { key: string; direction: 'asc' | 'desc' } | null;
type SidView = { valueFilter: 'Value' | '%'; selectedTickers: string[]; itemsPerPage: number };

const toRelMonthKey = (key: string, months: string[]): string => {
  const idx = months.indexOf(key);
  return idx >= 0 ? monthAlias(months, idx) : key;
};

// M0/M1… → YYYYMM (null = that month is not shown); kept as M0 while the months are still loading
const fromRelMonthKey = (key: string, months: string[]): string | null =>
  REL_MONTH_RE.test(key) && months.length > 0 ? resolveMonthRef(key, months) : key;

const filtersFromPreset = (filters: Record<string, string>, months: string[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    const key = fromRelMonthKey(k, months);
    if (key) out[key] = v;
  }
  return out;
};

const sidPresetOf = (columnFilters: Record<string, string>, sortConfig: SidSort, months: string[], view: SidView): FilterSettings => {
  const filters: Record<string, string> = {};
  for (const [k, v] of Object.entries(cleanFilters(columnFilters))) filters[toRelMonthKey(k, months)] = v;
  return {
    filters,
    sort: sortConfig ? { key: toRelMonthKey(sortConfig.key, months), direction: sortConfig.direction } : null,
    view: { ...view },
  };
};

const formatCount = (n: number): string =>
  new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const formatNumberWithAbbreviation = (num: number | null): string => {
  if (num === null || num === 0) return '-';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    return sign + (absNum / 1e6).toFixed(1) + 'M';
  } else if (absNum >= 1e3) {
    return sign + (absNum / 1e3).toFixed(1) + 'K';
  } else {
    return sign + absNum.toFixed(1);
  }
};

const getPriceColorClass = (colorCode: number): string => {
  switch (colorCode) {
    case 0: return 'text-[#ff0000]';
    case 1: return 'text-[#ffa500]';
    case 2: return 'text-[#b2ff00]';
    case 3: return 'text-[#00ff00]';
    default: return 'text-gray-500';
  }
};

// Kode warna volume_color (backend) — sama persis dengan halaman Acc-Dist
const getVolumeColor = (comp: number): string => {
  switch (comp) {
    case 1: return 'text-[#00ff00]';
    case 2: return 'text-[#b2ff00]';
    case 3: return 'text-[#ff0000]';
    case 4: return 'text-[#ffa500]';
    default: return 'text-gray-300';
  }
};

// Warna % chg & Price: merah turun, hijau naik, netral datar — sama dgn menu Acc-Dist.
const getPercentChgColor = (chg: number): string => {
  if (chg > 0) return 'text-[#00ff00]';
  if (chg < 0) return 'text-[#ff0000]';
  return 'text-gray-300';
};

interface VsData {
  ratios: number[];
  ratio: number;
  comp: number;
}

// VSpike: streak D0..D4 dgn volume > 2× MA40 + ratio/comp D0 dari volume.zip
// (rumus sama persis dgn menu Acc-Dist).
const SPIKE_RATIO = 2;
const extractVsData = (rows: Record<string, unknown>[]): Record<string, VsData> => {
  const map: Record<string, VsData> = {};
  for (const item of rows) {
    const s = String(item['Ticker'] ?? item['Stock'] ?? '');
    if (!s) continue;
    map[s] = {
      ratios: ['0', '1', '2', '3', '4'].map(d => Number(item[`d${d}_vol_pct`]) || 0),
      ratio: Number(item['d0_vol_pct']) || 0,
      comp: Number(item['d0_compat']) || 0,
    };
  }
  return map;
};
const calcSpikeStreak = (vs: VsData | undefined): number | undefined => {
  if (!vs) return undefined;
  let n = 0;
  for (const r of vs.ratios) {
    if (r > SPIKE_RATIO) n++;
    else break;
  }
  return n;
};

const getVsColorClass = (vs: VsData | undefined, volColor: number, percent1d: number): string => {
  if (!vs) return getPriceColorClass(volColor);
  if (vs.comp === 0 && percent1d === 0) {
    if (vs.ratio > 5) return 'text-[#00ff00]';
    if (vs.ratio >= 2) return 'text-[#b2ff00]';
    return 'text-gray-300';
  }
  return getVolumeColor(vs.comp);
};

// Arah vs bulan sebelumnya: naik = hijau, turun = merah
const getDirColorClass = (delta: number | null): string => {
  if (delta === null || delta === 0) return 'text-gray-300';
  return delta > 0 ? 'text-[#00ff00]' : 'text-[#ff0000]';
};

const monthValueAt = (row: SidRowData, m: string | undefined): number | null => {
  if (!m) return null;
  const v = row.months[m];
  return v === undefined ? null : v;
};

// % perubahan vs bulan sebelumnya (null bila salah satu bulan tidak ada / prev 0)
const monthPctAt = (row: SidRowData, months: string[], idx: number): number | null => {
  if (idx <= 0) return null;
  const cur = monthValueAt(row, months[idx]);
  const prev = monthValueAt(row, months[idx - 1]);
  if (cur === null || prev === null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
};

// Warna sel kolom bulan — sama persis dgn renderMonthCell (untuk filter warna).
const getMonthColorClass = (row: SidRowData, months: string[], m: string, valueFilter: string): string => {
  const mIdx = months.indexOf(m);
  const cur = monthValueAt(row, m);
  if (cur === null) return 'text-gray-600';
  if (valueFilter === '%') {
    const pct = mIdx >= 0 ? monthPctAt(row, months, mIdx) : null;
    return pct === null ? 'text-gray-600' : getDirColorClass(pct);
  }
  const prev = mIdx > 0 ? monthValueAt(row, months[mIdx - 1]) : null;
  const delta = prev !== null && prev !== 0 ? cur - prev : null;
  return getDirColorClass(delta);
};

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeRef.current(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function StorySid() {
  const [data, setData] = useState<SidRowData[]>([]);
  const [months, setMonths] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [stockInput, setStockInput] = useState('');
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [valueFilter, setValueFilter] = useState<'Value' | '%'>('Value');
  const [formValueFilter, setFormValueFilter] = useState<'Value' | '%'>('Value');
  const [formSelectedTickers, setFormSelectedTickers] = useState<string[]>([]);

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [volRatioMap, setVolRatioMap] = useState<Record<string, VsData>>({});
  const [fetchTrigger, setFetchTrigger] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isRefetching, setIsRefetching] = useState(false);

  // Load data
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      try {
        if (data.length === 0) {
          setLoading(true);
        } else {
          setIsRefetching(true);
        }
        setError(null);

        const volPart1Promise = fetchVolumeData('all', 1);
        const volPart2Promise = fetchVolumeData('all', 2);

        const res = await fetchSidData();
        if (!active) return;

        const visibleMonths = [...res.data.months].sort().slice(-24);
        setMonths(visibleMonths);
        setData(convertBackendToFrontend(res.data.rows, visibleMonths));
        setLoading(false);
        setIsRefetching(false);

        // VSpike: merge ratio D0..D4 + ratio/comp D0 (volume.zip) → map symbol → VsData
        Promise.all([volPart1Promise, volPart2Promise]).then(([vol1, vol2]) => {
          if (!active) return;
          const map = extractVsData(vol1.data.accumulationData || []);
          Object.assign(map, extractVsData(vol2.data.accumulationData || []));
          if (map['IDX']) map['IHSG'] = map['IDX'];
          if (map['IHSG']) map['IDX'] = map['IHSG'];
          setVolRatioMap(map);
        }).catch(() => {});
      } catch (err: any) {
        if (!active) return;
        console.error('Error SID data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
        setIsRefetching(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [fetchTrigger]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique tickers for filter dropdown — only IHSG + 4-char
  const allTickers = useMemo(() => {
    return [...new Set(data.map(item => item.symbol).filter(s => s === 'IHSG' || s.length === 4))].sort();
  }, [data]);

  const filteredTickers = useMemo(() => {
    return stockInput
      ? allTickers.filter(ticker => ticker.toLowerCase().includes(stockInput.toLowerCase()))
      : allTickers;
  }, [allTickers, stockInput]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIndicator = ({ col }: { col: string }) => {
    if (!sortConfig || sortConfig.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const filterAndSortData = () => {
    let filteredBase = data.filter(item => item.symbol === 'IHSG' || item.symbol.length === 4);
    const selectedSet = new Set(selectedTickers);
    let filtered = selectedTickers.length === 0
      ? filteredBase
      : filteredBase.filter(item => selectedSet.has(item.symbol));

    // Filter per kolom: ekspresi `A and B or C` + token warna (merah/oren/ijo/lime/netral/kuning)
    const pass = (expr: string | undefined, cell: FilterCell): boolean => {
      if (!expr || !expr.trim()) return true;
      return evaluateFilterExpression(expr, cell);
    };

    filtered = filtered.filter(item => {
      const priceColor = item.price === 0 ? 'text-gray-600' : getPercentChgColor(item.percent1d);
      const vs = volRatioMap[item.symbol];
      const vsStreak = calcSpikeStreak(vs);
      const vsCellColor = vsStreak ? getVsColorClass(vs, item.vol_color, item.percent1d) : 'text-gray-600';
      const volColColor = (item.volume === 0 || item.price === 0) ? 'text-gray-600' : getVsColorClass(vs, item.vol_color, item.percent1d);
      const trendColor = item.trend === 'Up Trend' ? 'text-[#00ff00]' : item.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500';
      const momentumStr = item.momentum === 2 ? 'up' : item.momentum === 0 ? 'down' : 'sw';
      const momentumColor = item.momentum === 2 ? 'text-[#00ff00]' : item.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500';
      const insiderColor = item.insider === undefined ? 'text-gray-600' : item.insider > 0 ? 'text-[#00ff00]' : item.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]';

      if (!pass(columnFilters['symbol'], { value: NaN, text: item.symbol.toLowerCase(), colorClass: 'text-gray-300', isNumeric: false })) return false;
      if (!pass(columnFilters['trend'], { value: NaN, text: item.trend.toLowerCase(), colorClass: trendColor, isNumeric: false })) return false;
      if (!pass(columnFilters['momentum'], { value: NaN, text: momentumStr, colorClass: momentumColor, isNumeric: false })) return false;
      if (!pass(columnFilters['insider'], { value: item.insider ?? 0, text: String(item.insider ?? 0), colorClass: insiderColor, isNumeric: true })) return false;
      if (!pass(columnFilters['vs'], { value: vsStreak ?? 0, text: String(vsStreak ?? 0), colorClass: vsCellColor, isNumeric: true })) return false;
      if (!pass(columnFilters['percent1d'], { value: item.percent1d, text: String(item.percent1d), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters['price'], { value: item.price, text: String(item.price), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters['volume'], { value: item.volume, text: String(item.volume), colorClass: volColColor, isNumeric: true })) return false;
      if (!pass(columnFilters['value'], { value: item.value, text: String(item.value), colorClass: volColColor, isNumeric: true })) return false;

      const monthRefAtom: AtomEvaluator = (atom, c) => {
        const ref = parseCrossColumnReference(atom, MONTH_REF_RE, { requireParens: true });
        if (!ref) return null;
        const ym = resolveMonthRef(ref.ref, months);
        let refVal = 0;
        if (ym) {
          if (valueFilter === '%') {
            const rIdx = months.indexOf(ym);
            refVal = rIdx >= 0 ? monthPctAt(item, months, rIdx) ?? 0 : 0;
          } else {
            refVal = item.months[ym] ?? 0;
          }
        }
        return evaluateCrossColumn(c.value, refVal, ref);
      };

      for (const m of months) {
        const expr = columnFilters[m];
        if (!expr || !expr.trim()) continue;
        const mIdx = months.indexOf(m);
        const raw = item.months[m];
        const cellVal = valueFilter === '%'
          ? (mIdx >= 0 ? monthPctAt(item, months, mIdx) : null)
          : raw;
        const cell: FilterCell = {
          value: cellVal ?? NaN,
          text: cellVal == null ? '' : String(cellVal),
          colorClass: getMonthColorClass(item, months, m, valueFilter),
          isNumeric: true,
        };
        if (!evaluateFilterExpression(expr, cell, { customAtom: monthRefAtom })) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (!sortConfig) return 0;
      let aValue: any;
      let bValue: any;

      if (sortConfig.key === 'symbol') {
        aValue = a.symbol;
        bValue = b.symbol;
      } else if (sortConfig.key === 'price') {
        aValue = a.price;
        bValue = b.price;
      } else if (sortConfig.key === 'percent1d') {
        aValue = a.percent1d;
        bValue = b.percent1d;
      } else if (sortConfig.key === 'volume') {
        aValue = a.volume;
        bValue = b.volume;
      } else if (sortConfig.key === 'vs') {
        aValue = calcSpikeStreak(volRatioMap[a.symbol]) ?? -1;
        bValue = calcSpikeStreak(volRatioMap[b.symbol]) ?? -1;
      } else if (sortConfig.key === 'value') {
        aValue = a.value;
        bValue = b.value;
      } else if (sortConfig.key === 'trend') {
        aValue = a.trend;
        bValue = b.trend;
      } else if (sortConfig.key === 'momentum') {
        aValue = a.momentum;
        bValue = b.momentum;
      } else if (sortConfig.key === 'insider') {
        const av = a.insider;
        const bv = b.insider;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        aValue = av;
        bValue = bv;
      } else {
        const mIdx = months.indexOf(sortConfig.key);
        if (mIdx >= 0) {
          aValue = valueFilter === '%'
            ? monthPctAt(a, months, mIdx)
            : monthValueAt(a, sortConfig.key);
          bValue = valueFilter === '%'
            ? monthPctAt(b, months, mIdx)
            : monthValueAt(b, sortConfig.key);
        }
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const table1Ref = useRef<HTMLDivElement>(null);
  const table2Ref = useRef<HTMLDivElement>(null);

  const handleScroll = (sourceIndex: number, e: React.UIEvent<HTMLDivElement>) => {
    const targetRef = sourceIndex === 0 ? table2Ref : table1Ref;
    if (targetRef.current) {
      if (targetRef.current.scrollTop !== e.currentTarget.scrollTop) {
        targetRef.current.scrollTop = e.currentTarget.scrollTop;
      }
      if (targetRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
        targetRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }
    }
  };

  const sortedData = useMemo(() => filterAndSortData(), [data, months, selectedTickers, columnFilters, sortConfig, valueFilter, volRatioMap]);

  const renderTable = (tableIndex: number) => {
    const idxRow = sortedData.find(r => r.symbol === 'IHSG' || r.symbol === 'IDX');
    const remainingData = sortedData.filter(r => r.symbol !== 'IHSG' && r.symbol !== 'IDX');
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = remainingData.slice(startIndex, endIndex);

    const renderMonthCell = (row: SidRowData, m: string, mIdx: number) => {
      const cur = monthValueAt(row, m);
      if (cur === null) {
        return <td key={m} className="px-1 py-0 text-right text-gray-600">-</td>;
      }
      const prev = mIdx > 0 ? monthValueAt(row, months[mIdx - 1]) : null;
      const delta = prev !== null && prev !== 0 ? cur - prev : null;
      const colorClass = getDirColorClass(delta);

      if (valueFilter === '%') {
        const pct = monthPctAt(row, months, mIdx);
        if (pct === null) {
          return <td key={m} className="px-1 py-0 text-right text-gray-600">-</td>;
        }
        return (
          <td key={m} className={`px-1 py-0 text-right truncate ${getDirColorClass(pct)}`}>
            {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
          </td>
        );
      }
      const isIdxRow = row.symbol === 'IHSG' || row.symbol === 'IDX';
      return (
        <td key={m} className={`px-1 py-0 text-right truncate ${colorClass}`}>
          {isIdxRow ? formatNumberWithAbbreviation(cur) : formatCount(cur)}
        </td>
      );
    };

    const renderRow = (row: SidRowData, isIdx: boolean = false) => {
      const vs = volRatioMap[row.symbol];
      const vsStreak = calcSpikeStreak(vs);
      return (
      <tr key={row.symbol} className={`group hover:bg-white/[0.25] transition-colors h-[18px] divide-x divide-white/30 ${isIdx ? 'bg-blue-900/30' : ''}`}>
        <td className={`px-1 py-0 text-left text-gray-300 truncate sticky left-0 z-[5] ${isIdx ? 'bg-[#091129]' : 'bg-black'} group-hover:bg-[#404040]`}>{row.symbol === 'IDX' ? 'IHSG' : row.symbol}</td>
        <td className={`px-1 py-0 text-center truncate sticky left-[60px] z-[5] ${isIdx ? 'bg-[#091129]' : 'bg-black'} group-hover:bg-[#404040] ${row.trend === 'Up Trend' ? 'text-[#00ff00]' : row.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.trend === 'Up Trend' ? 'UP' : row.trend === 'Down Trend' ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-center truncate sticky left-[120px] z-[5] ${isIdx ? 'bg-[#091129]' : 'bg-black'} group-hover:bg-[#404040] ${row.momentum === 2 ? 'text-[#00ff00]' : row.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.momentum === 2 ? 'UP' : row.momentum === 0 ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-right truncate sticky left-[198px] z-[5] ${isIdx ? 'bg-[#091129]' : 'bg-black'} group-hover:bg-[#404040] ${row.insider === undefined ? 'text-gray-600' : row.insider > 0 ? 'text-[#00ff00]' : row.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]'}`}>
          {row.insider === undefined ? '-' : formatNumberWithAbbreviation(row.insider)}
        </td>
        <td className={`px-1 py-0 text-right truncate sticky left-[268px] z-[5] ${isIdx ? 'bg-[#091129]' : 'bg-black'} group-hover:bg-[#404040] ${vsStreak ? getVsColorClass(vs, row.vol_color, row.percent1d) : 'text-gray-600'}`}>
          {vsStreak ? `${vsStreak}X` : '-'}
        </td>
        {months.map((m, mIdx) => renderMonthCell(row, m, mIdx))}
        <td className={`px-1 py-0 text-right truncate ${row.price === 0 ? 'text-gray-600' : getPercentChgColor(row.percent1d)}`}>
          {row.price === 0 ? '-' : row.percent1d.toFixed(1) + '%'}
        </td>
        <td className={`px-1 py-0 text-right truncate ${row.price === 0 ? 'text-gray-600' : getPercentChgColor(row.percent1d)}`}>
          {row.price === 0 ? '-' : formatCount(row.price)}
        </td>
        <td className={`px-1 py-0 text-right truncate ${row.volume === 0 || row.price === 0 ? 'text-gray-600' : getVsColorClass(vs, row.vol_color, row.percent1d)}`}>
          {row.volume === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.volume)}
        </td>
        <td className={`px-1 py-0 text-right truncate ${row.value === 0 || row.price === 0 ? 'text-gray-600' : getVsColorClass(vs, row.vol_color, row.percent1d)}`}>
          {row.value === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.value)}
        </td>
      </tr>
      );
    };

    return (
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full border-t border-white/30">
        <div
          className="flex-auto overflow-y-auto overflow-x-auto"
          style={{ willChange: 'transform' }}
          ref={tableIndex === 0 ? table1Ref : table2Ref}
          onScroll={(e) => handleScroll(tableIndex, e)}
        >
          <table className="w-full text-[12px] tabular-nums table-fixed leading-tight border-separate border-spacing-0" style={{ minWidth: FIXED_COLS_WIDTH + months.length * MONTH_COL_WIDTH }}>
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
              <tr className="cursor-pointer select-none h-[22px] divide-x divide-white/30">
                <th onClick={() => handleSort('symbol')} className="px-1 py-0 text-center w-[60px] sticky left-0 z-10 bg-[#0d131f]">Ticker<SortIndicator col="symbol" /></th>
                <th onClick={() => handleSort('trend')} className="px-1 py-0 text-center w-[60px] sticky left-[60px] z-10 bg-[#0d131f]">Trend<SortIndicator col="trend" /></th>
                <th onClick={() => handleSort('momentum')} className="px-1 py-0 text-center w-[78px] sticky left-[120px] z-10 bg-[#0d131f]">Momentum<SortIndicator col="momentum" /></th>
                <th onClick={() => handleSort('insider')} className="px-1 py-0 text-center w-[70px] sticky left-[198px] z-10 bg-[#0d131f]">Insider<SortIndicator col="insider" /></th>
                <th onClick={() => handleSort('vs')} className="px-1 py-0 text-center w-[60px] sticky left-[268px] z-10 bg-[#0d131f]" title="Hari spike >2X beruntun dari D0 (maks 5 hari)">VSpike<SortIndicator col="vs" /></th>
                {months.map((m, mIdx) => (
                  <th key={m} onClick={() => handleSort(m)} title={`${monthAlias(months, mIdx)} - ${monthLabel(m)}`} className={`px-1 py-0 text-center bg-purple-500/20`}>{monthLabel(m)}<SortIndicator col={m} /></th>
                ))}
                <th onClick={() => handleSort('percent1d')} className="px-1 py-0 text-center w-[60px] bg-emerald-500/20">%<SortIndicator col="percent1d" /></th>
                <th onClick={() => handleSort('price')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Price<SortIndicator col="price" /></th>
                <th onClick={() => handleSort('volume')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Volume<SortIndicator col="volume" /></th>
                <th onClick={() => handleSort('value')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Value<SortIndicator col="value" /></th>
              </tr>
              {/* Interactive filter row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                <td className="px-1 py-0 sticky left-0 z-10 bg-[#1c2532]">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-left text-white placeholder:text-gray-500 focus:outline-none" placeholder="Filter" value={columnFilters['symbol'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, symbol: val}))} />
                </td>
                <td className="px-1 py-0 sticky left-[60px] z-10 bg-[#1c2532]">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters['trend'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, trend: val}))} />
                </td>
                <td className="px-1 py-0 sticky left-[120px] z-10 bg-[#1c2532]">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters['momentum'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, momentum: val}))} />
                </td>
                <td className="px-1 py-0 sticky left-[198px] z-10 bg-[#1c2532]">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1B" value={columnFilters['insider'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, insider: val}))} />
                </td>
                <td className="px-1 py-0 sticky left-[268px] z-10 bg-[#1c2532]">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">=2" value={columnFilters['vs'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, vs: val}))} />
                </td>
                {months.map((m) => (
                  <td key={m} className="px-1 py-0">
                    <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder={valueFilter === '%' ? '>5' : '>100K'} title={valueFilter === '%' ? '>5, >=2.5, <0, >(M1/2), <(M1*50%)' : '>100K, >=50K, <1000, >(M1/2), <(M1*50%)'} value={columnFilters[m] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, [m]: val}))} />
                  </td>
                ))}
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">5" value={columnFilters['percent1d'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, percent1d: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder="<200" value={columnFilters['price'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, price: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1M" value={columnFilters['volume'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, volume: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1B" value={columnFilters['value'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, value: val}))} />
                </td>
              </tr>
            </thead>
            <tbody className="divide-y-0">
              {idxRow && renderRow(idxRow, true)}
              {paginatedData.map((row) => renderRow(row))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const resetSettings = () => {
    setSortConfig(null);
    setStockInput('');
    setSelectedTickers([]);
    setFormSelectedTickers([]);
    setShowStockSuggestions(false);
    setValueFilter('Value');
    setFormValueFilter('Value');
    setColumnFilters({});
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleShow = () => {
    setSelectedTickers(formSelectedTickers);
    setValueFilter(formValueFilter);
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleTickerSelect = (ticker: string) => {
    if (formSelectedTickers.includes(ticker)) {
      setFormSelectedTickers(formSelectedTickers.filter(t => t !== ticker));
    } else {
      setFormSelectedTickers([...formSelectedTickers, ticker]);
    }
  };

  // Presets: filter row + sort (month columns as M0/M1…) + Data, tickers and rows per page as applied by Show
  const presetCurrent = useMemo(
    () => sidPresetOf(columnFilters, sortConfig, months, { valueFilter, selectedTickers, itemsPerPage }),
    [columnFilters, sortConfig, months, valueFilter, selectedTickers, itemsPerPage],
  );

  // Data/tickers are applied client-side (the fetch takes no options), so a preset never needs a refetch
  const applyPreset = (s: FilterSettings) => {
    const view: Record<string, unknown> = s.view && typeof s.view === 'object' ? s.view : {};
    const nextFilters = filtersFromPreset(cleanFilters(s.filters), months);
    const sort = s.sort;
    const sortKey = sort && typeof sort.key === 'string' && (sort.direction === 'asc' || sort.direction === 'desc')
      ? fromRelMonthKey(sort.key, months) : null;
    const nextSort: SidSort = sort && sortKey ? { key: sortKey, direction: sort.direction } : null;
    const vf = view['valueFilter'];
    const nextValueFilter = vf === 'Value' || vf === '%' ? vf : valueFilter;
    const tickers = view['selectedTickers'];
    const nextTickers = Array.isArray(tickers)
      ? [...new Set(tickers.filter((t): t is string => typeof t === 'string' && t.trim() !== ''))]
      : selectedTickers;
    const perPage = view['itemsPerPage'];
    const nextItemsPerPage = typeof perPage === 'number' && ITEMS_PER_PAGE_OPTIONS.includes(perPage) ? perPage : itemsPerPage;

    const next = sidPresetOf(nextFilters, nextSort, months, { valueFilter: nextValueFilter, selectedTickers: nextTickers, itemsPerPage: nextItemsPerPage });
    if (settingsKey(next) !== settingsKey(presetCurrent)) setCurrentPage(1);
    setColumnFilters(nextFilters);
    setSortConfig(nextSort);
    setValueFilter(nextValueFilter);
    setFormValueFilter(nextValueFilter);
    setSelectedTickers(nextTickers);
    setFormSelectedTickers(nextTickers);
    setItemsPerPage(nextItemsPerPage);
  };

  // A preset picked before the months loaded keeps its M0/M1… keys; put them on the real months once they are in
  useEffect(() => {
    if (months.length === 0) return;
    setColumnFilters(prev => Object.keys(prev).some(k => REL_MONTH_RE.test(k)) ? filtersFromPreset(prev, months) : prev);
    setSortConfig(prev => {
      if (!prev || !REL_MONTH_RE.test(prev.key)) return prev;
      const key = resolveMonthRef(prev.key, months);
      return key ? { ...prev, key } : null;
    });
  }, [months]);

  const presetLabel = (key: string): string => {
    const known = COLUMN_LABELS[key];
    if (known) return known;
    const ym = REL_MONTH_RE.test(key) ? resolveMonthRef(key, months) : key;
    return ym ? monthLabel(ym) : key;
  };

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <p className="text-red-500 mb-4 text-sm">Error: {error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" size="sm">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-2 relative">
      {(isRefetching || loading) && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }}></div>
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      {/* Top Control Bar */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-7 md:gap-y-2">

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
              <div className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <input
                  type="text"
                  className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate"
                  placeholder="Select ticker..."
                  value={showStockSuggestions ? stockInput : (formSelectedTickers.length > 0 ? formSelectedTickers.join(', ') : '')}
                  onChange={(e) => {
                    setStockInput(e.target.value);
                    setShowStockSuggestions(true);
                  }}
                  onFocus={() => setShowStockSuggestions(true)}
                />
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
              </div>
              {showStockSuggestions && (
                <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-full overflow-hidden flex flex-col">
                  {formSelectedTickers.length > 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                      <span>Selected ({formSelectedTickers.length})</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormSelectedTickers([]);
                        }}
                        className="text-xs text-destructive hover:text-destructive/80 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="max-h-60 overflow-y-auto py-1">
                    {(() => {
                      const filteredList = stockInput === ''
                        ? allTickers.slice(0, 30)
                        : filteredTickers.slice(0, 30);
                      return filteredList.map(stock => {
                        const isSelected = formSelectedTickers.includes(stock);
                        return (
                          <div
                            key={stock}
                            className={`px-3 py-[2.06px] text-sm cursor-pointer hover:bg-accent flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'text-foreground'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTickerSelect(stock);
                            }}
                          >
                            <span>{stock}</span>
                            {isSelected && <span className="text-primary text-xs">✓</span>}
                          </div>
                        );
                      });
                    })()}
                    {stockInput !== '' && filteredTickers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">No stocks found</div>
                    )}
                    {stockInput === '' && allTickers.length > 30 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t border-[#3a4252]">
                        Type to search {allTickers.length - 30} more stocks
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Data:</label>
            <div className="flex items-center gap-1">
              <select
                value={formValueFilter}
                onChange={(e) => {
                  setFormValueFilter(e.target.value as 'Value' | '%');
                }}
                className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
              >
                <option value="Value">Value</option>
                <option value="%">%</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto ml-auto">
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleShow} className="h-8 text-sm px-4">
                Show
              </Button>
              <Button onClick={resetSettings} variant="outline" size="sm" className="flex items-center gap-2 h-8">
                <Undo2 className="w-4 h-4 rotate-180" />
                Reset
              </Button>
              {/* Right edge on phones so the w-80 dropdown (right-aligned) stays on screen */}
              <div className="ml-auto sm:ml-0">
                <FilterPresets page="sid" current={presetCurrent} onApply={applyPreset} label={presetLabel} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full animate-pulse">
          <div className="h-[26px] w-full bg-gray-900 border-b border-white/30 shrink-0 flex items-center px-2">
            <div className="h-3 w-16 bg-slate-700/40 rounded"></div>
          </div>
          <div className="flex-1 p-2 space-y-3">
            {/* Header row */}
            <div className="flex gap-2 pb-2 border-b border-white/20">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="h-4 flex-1 bg-slate-700/30 dark:bg-slate-600/50 rounded"></div>
              ))}
            </div>
            {/* Data rows */}
            {[...Array(20)].map((_, i) => (
              <div key={i} className="flex gap-2">
                <div className="h-3.5 w-12 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                {[...Array(11)].map((_, j) => (
                  <div key={j} className="h-3.5 flex-1 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      ) : (
      <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full">
        <div className="flex flex-col min-h-0 flex-auto w-full">
          {renderTable(0)}
        </div>

        {/* Pagination Controls */}
        {(sortedData.length > 0) && (
          <div className="border-t border-border bg-muted/30 px-2 py-1 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-1 py-0.5 text-[11px] border border-border rounded-md bg-background text-foreground h-6"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>
                Showing {startIndex + 1} - {Math.min(endIndex, sortedData.length)} of {sortedData.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="First page"
              >
                <ChevronFirst className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-[11px] text-foreground px-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Last page"
              >
                <ChevronLast className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
